import React, { useState, useEffect } from 'react';
import {
  Box, Typography, TextField, Button, Grid, Paper, Checkbox, IconButton,
  FormControl, InputLabel, Select, MenuItem, Stack, Accordion,
  AccordionSummary, CircularProgress,
  AccordionDetails, Tooltip, FormHelperText, Pagination,
  InputAdornment
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import SearchIcon from '@mui/icons-material/Search';
import { Edit, DeleteOutline } from '@mui/icons-material';
import { Add } from '@mui/icons-material';
import AddLocationAltIcon from '@mui/icons-material/AddLocationAlt';
import RightDrawer from '../../components/RightDrawer';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { GET_LISTBYID, DELETE_DATA, GET_LIST, SAVE, UPDATE } from '../../utils/api';
import DeleteConfirmationDialog from '../../components/DeleteConfirmationDialog';
import { Alert, Snackbar } from '@mui/material';
import { AddCircleOutline, RemoveCircleOutline } from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';


const DeviceVendorMapping = () => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [filterLocation, setFilterLocation] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [alert, setAlert] = useState({ type: '', message: '', open: false });
  const [searchText, setSearchText] = useState('');
  const [errors, setErrors] = useState({
    vendor: '',
    currency: '',
    locations: [],
    timePricePairs: []
  });
  const [vendors, setVendors] = useState([]);
  const [devicesList, setDevicesList] = useState([]);
  const [vendor, setVendor] = useState('');
  const [locations, setLocations] = useState([]);
  const [formState, setFormState] = useState({
    currency: '',
    timePricePairs: [{ time: '', price: '' }],
  });
  const [editId, setEditId] = useState(null);
  const { logout } = useAuth();
  const navigate = useNavigate();
  const currencySymbols = {
    USD: '$',
    INR: '₹',
    JPY: '¥',
  };
  const [totalPages, setTotalPages] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  const fetchInventory = async (searchText = '') => {
    setLoading(true);
    try {

      let queryParams = {
        ...(searchText ? { searchText } : {}),
        limit: limit
      };
      const data = await GET_LIST('/vendor-device-mappings', queryParams);
      if (data.statusCode == 401) {
        logout();
        navigate('/login');
      }
      setRows(data.data);
      setTotalPages(data.data.totalPages)
      console.log(data.data)
    } catch (err) {
      console.error('Failed to fetch inventory:', err);
    } finally {
      setLoading(false);
    }
  };

  const formattedRows = rows.map((row) => ({
    ...row,
    id: row._id,
  }));

  // Delete dialog handlers
  const handleOpenDeleteDialog = (id) => {
    setSelectedId(id);
    setDeleteDialogOpen(true);
  };
  const handleCloseDeleteDialog = () => {
    setSelectedId(null);
    setDeleteDialogOpen(false);
  };
  const handleConfirmDelete = async () => {
    try {
      await DELETE_DATA('/vendor-device-mappings', selectedId);
      setAlert({ type: 'success', message: 'Vendor deleted successfully.', open: true });
      await fetchInventory(searchText);
    } catch (error) {
      setAlert({ type: 'error', message: 'Failed to delete the vendor.', open: true });
    } finally {
      handleCloseDeleteDialog();
    }
  };

  const fetchData = async () => {
    try {
      const [vendRes, devRes] = await Promise.all([
        GET_LISTBYID('/vendors/list'),
        GET_LISTBYID('/device-inventory/all/non-paginated'),
      ]);
      setVendors((vendRes || []).filter(vendor => vendor.active === true));
      setDevicesList(devRes || []);
    } catch (err) {
      console.error('Failed to fetch vendors or devices:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInventory(searchText);
    fetchData();
  }, []);

  const columns = [
    { field: 'vendorId', headerName: 'Vendor ID', flex: 1 },
    { field: 'vendorName', headerName: 'Vendor Name', flex: 1 },
    { field: 'mappedLocation', headerName: 'Mapped Location', flex: 1 },
    { field: 'mappedDevices', headerName: 'Mapped Devices', flex: 1 },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 1,
      sortable: false,
      renderCell: (params) => (
        <>
          <Tooltip title="Edit">
            <IconButton
              size="small"
              onClick={() => handleEdit(params.row._id)}
            >
              <Edit fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton
              size="small"
              sx={{ color: 'red' }}
              onClick={(e) => {
                e.stopPropagation();
                handleOpenDeleteDialog(params.row.id);
              }}
            >
              <DeleteOutline fontSize="small" />
            </IconButton>
          </Tooltip>
        </>
      ),
    },
  ];

  const handleOpenDrawer = () => setDrawerOpen(true);
  const handleCloseDrawer = () => {
    setDrawerOpen(false)
    setVendor('');
    setLocations([]);
    setFormState({
      currency: '',
      timePricePairs: [{ time: '', price: '' }],
    });
    setEditId(null);
  };

  const handleEdit = async (mappingId) => {
    try {
      const data = await GET_LISTBYID("/vendor-device-mappings", mappingId);
      setEditId(data._id);
      setVendor(data.vendor_id);
      console.log(data);
      setLocations(
        data.locations.map((loc) => ({
          locationName: loc.name,
          latitude: loc.latitude,
          longitude: loc.longitude,
          devices: loc.devices,
          saved: true,
        }))
      );
      setFormState({
        currency: data.currency,
        timePricePairs: Object.entries(data.pricingMaster).map(([time, price]) => ({
          time,
          price: price.toString(),
        })),
      });
      setDrawerOpen(true);
    } catch (error) {
      console.error('Error fetching mapping:', error);
    }
  };

  const handleSaveMapping = async () => {
    const newErrors = {
      vendor: '',
      currency: '',
      locations: [],
      timePricePairs: [],
    };

    let hasError = false;

    // Validate Vendor
    if (!vendor) {
      newErrors.vendor = 'Vendor is required.';
      hasError = true;
    }

    // Validate Locations
    if (locations.length === 0) {
      setAlert({ type: 'error', message: 'At least one location is required.', open: true });
      return;
    }

    locations.forEach((loc, idx) => {
      const locErrors = { locationName: '', latitude: '', longitude: '', devices: '' };

      if (!loc.locationName) {
        locErrors.locationName = 'Location name is required.';
        hasError = true;
      }
      if (!loc.latitude) {
        locErrors.latitude = 'Latitude is required.';
        hasError = true;
      }
      if (!loc.longitude) {
        locErrors.longitude = 'Longitude is required.';
        hasError = true;
      }
      if (!loc.devices || loc.devices.length === 0) {
        locErrors.devices = 'At least one device must be selected.';
        hasError = true;
      }

      newErrors.locations[idx] = locErrors;
    });

    // Validate Currency
    if (!formState.currency) {
      newErrors.currency = 'Currency is required.';
      hasError = true;
    }

    // Validate Time-Price Pairs
    formState.timePricePairs.forEach((pair, i) => {
      const pairError = { time: '', price: '' };
      if (!pair.time) {
        pairError.time = 'Time is required.';
        hasError = true;
      }
      if (!pair.price) {
        pairError.price = 'Price is required.';
        hasError = true;
      }
      newErrors.timePricePairs[i] = pairError;
    });

    setErrors(newErrors);
    if (hasError) return;

    // === BUILD PAYLOAD ===
    const pricingMaster = {};
    formState.timePricePairs.forEach(({ time, price }) => {
      pricingMaster[time] = Number(price);
    });

    const payload = {
      vendor_id: vendor,
      locations: locations.map((loc) => ({
        name: loc.locationName,
        latitude: loc.latitude,
        longitude: loc.longitude,
        devices: loc.devices,
      })),
      pricingMaster,
      currency: formState.currency,
    };

    try {
      let res;
      if (editId) {
        res = await UPDATE(`/vendor-device-mappings/${editId}`, payload);
      } else {
        res = await SAVE("/vendor-device-mappings", payload);
      }

      if (res.statusCode == 401) {
        logout();
        navigate('/login');
      }

      if (res.status === 200 || res.status === 201) {
        setAlert({
          type: 'success',
          message: editId ? 'Vendor mapping edited successfully' : 'Vendor mapping saved successfully!',
          open: true,
        });
        await fetchInventory(searchText);
        handleCloseDrawer();
      } else {
        setAlert({
          type: 'error',
          message: res?.data?.message || `Failed with status ${res?.data?.message}`,
          open: true,
        });
      }
    } catch (error) {
      console.error('Error saving vendor mapping:', error);
      setAlert({
        type: 'error',
        message: 'Something went wrong while saving the mapping.',
        open: true,
      });
    }
  };


  const updateField = (field, value) => {
    setFormState((prev) => ({ ...prev, [field]: value }));
  };

  // TIme price functions 
  const handleTimePrice = (index, key, value) => {
    const updatedPairs = [...formState.timePricePairs];
    updatedPairs[index][key] = value;
    setFormState((prev) => ({ ...prev, timePricePairs: updatedPairs }));
  };

  const handleAddTimePrice = () => {
    setFormState((prev) => ({
      ...prev,
      timePricePairs: [...prev.timePricePairs, { time: '', price: '' }],
    }));
  };

  const handleRemoveTimePrice = (index) => {
    const updatedPairs = formState.timePricePairs.filter((_, i) => i !== index);
    setFormState((prev) => ({ ...prev, timePricePairs: updatedPairs }));
  };


  const handleAddLocation = () => {
    setLocations((prev) => [
      ...prev,
      { locationName: '', latitude: '', longitude: '', devices: [], saved: false },
    ]);
  };

  const handleLocationChange = (index, key, value) => {
    const updated = [...locations];
    updated[index][key] = value;
    setLocations(updated);
  };

  const handleSaveLocation = (index) => {
    const updated = [...locations];
    updated[index].saved = true;
    setLocations(updated);
  };

  // Exclude already selected devices in other locations
  const getAvailableDevices = (currentIndex) => {
    const selectedIds = locations
      .filter((_, i) => i !== currentIndex)
      .flatMap((loc) => loc.devices); // devices now contain only _id strings

    return devicesList.filter((d) => !selectedIds.includes(d._id));
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ color: 'black', fontWeight: 'bold' }}>
        Device Vendor Mapping
      </Typography>
      <Typography variant="body2" sx={{ color: 'gray', marginBottom: 1 }}>
        Last Updated: {new Date().toLocaleDateString()}
      </Typography>

      <Grid
        container
        spacing={0.5}
        sx={{ mb: 1 }}
        alignItems="center"
        justifyContent="flex-end"
        wrap="nowrap"
      >
        {/* Search Field */}
        <Grid item>
          <TextField
            id="outlined-size-small"
            label="Search"
            variant="outlined"
            size="small"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                fetchInventory(searchText);
              }
            }}
            fullWidth
            InputProps={{
              startAdornment: <SearchIcon sx={{ color: 'gray', mr: 1 }} />,
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '10px',
              },
            }}
          />
        </Grid>

        {/* <Grid item>
          <FormControl fullWidth size="small">
            <InputLabel>Filter by Location</InputLabel>
            <Select
              sx={{
                width: "180px"
              }}
              value={filterLocation}
              label="Filter by Location"
              onChange={(e) => setFilterLocation(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="Location1">Location1</MenuItem>
              <MenuItem value="Location2">Location2</MenuItem>
              <MenuItem value="Location3">Location3</MenuItem>
            </Select>
          </FormControl>
        </Grid> */}

        {selected.length > 0 && (
          <Grid item>
            <Button
              variant='contained'
              size="small"
              color="primary"
              onClick={() => console.log('Download clicked', selected)}
            >
              Download
            </Button>
          </Grid>
        )}

        {/* Add Inventory Button */}
        <Grid item>
          <Button
            onClick={handleOpenDrawer}
            size="small"
            variant='contained'
            startIcon={<AddLocationAltIcon />}
          >
            Map Vendor
          </Button>
        </Grid>
      </Grid>

      <Paper sx={{ width: '100%', mb: 2, borderRadius: 2, overflow: 'hidden' }}>
        {loading ? (
          <Box sx={{ height: 'calc(100vh - 190px)', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <CircularProgress size={30} />
          </Box>
        ) : (
          <>
            <Box sx={{ height: 'calc(100vh - 210px)', width: '100%' }}>
              <DataGrid
                rows={formattedRows}
                columns={columns}
                hideFooterPagination
                checkboxSelection
                disableRowSelectionOnClick
                onSelectionModelChange={(newSelection) => setSelected(newSelection)}
                selectionModel={selected}
                sortingOrder={['asc', 'desc']}
                getRowClassName={(params) =>
                  params.indexRelativeToCurrentPage % 2 === 0 ? 'even-row' : 'odd-row'
                }
                sx={{
                  '& .MuiDataGrid-columnHeaders': { backgroundColor: '#f2f2f2' },
                  '& .even-row': { backgroundColor: '#f6f6f6' },
                  '& .odd-row': { backgroundColor: '#ffffff' },
                  '& .MuiDataGrid-footerContainer': {
                    minHeight: '0px'
                  },
                  border: 'none',
                }}
              />
            </Box>
            <div style={{ display: "flex", justifyContent: "flex-end", padding: "6px" }}>
              <Pagination
                count={totalPages}
                page={page}
                color="primary"
                onChange={(e, value) => setPage(value)}
                shape="rounded"
              />
            </div>
          </>
        )}
      </Paper>

      <DeleteConfirmationDialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        onConfirm={handleConfirmDelete}
        title="Confirm Delete"
        message="Are you sure you want to delete this vendor mapping?"
      />

      <RightDrawer
        open={drawerOpen}
        onClose={handleCloseDrawer}
        onSave={handleSaveMapping}
        title={editId ? "Edit Map Vendor to Devices" : "Map Vendor to Devices"}
      >
        <Stack spacing={1} sx={{ width: '100%' }}>
          {/* Vendor Dropdown */}
          <FormControl
            fullWidth
            size="small"
            error={!!errors.vendor}
            required
          >
            <InputLabel>Vendor Name</InputLabel>
            <Select
              value={vendor}
              label="Vendor Name"
              onChange={(e) => setVendor(e.target.value)}
            >
              {vendors.map((v) => (
                <MenuItem key={v._id} value={v._id}>
                  {v.vendor_name}
                </MenuItem>
              ))}
            </Select>
            {errors.vendor && <FormHelperText>{errors.vendor}</FormHelperText>}
          </FormControl>

          {/* Add Location Button */}
          <Box display="flex" justifyContent="flex-end">
            <Button
              startIcon={<Add />}
              onClick={handleAddLocation}
              sx={{ width: '100%', mb: 1 }}
              variant='contained'
            >
              Add Location
            </Button>
          </Box>

          {/* Location Accordions */}
          <Box
            sx={{
              maxHeight: 350,
              overflow: 'auto',
              '&::-webkit-scrollbar': { display: 'none' },
            }}
          >
            {locations.map((loc, idx) => (
              <Accordion
                key={idx}
                defaultExpanded
                sx={{
                  backgroundColor: '#f5f5f5',           // light grey background
                  border: '1px solid rgba(0,0,0,0.12)',  // subtle border
                  borderRadius: 2,                       // theme.spacing(2) ~ 16px
                  mb: 1,
                  // Remove the default outline on expand
                  '&.Mui-expanded': {
                    margin: 0
                  },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    // match the border-radius on top corners
                    borderTopLeftRadius: 8,
                    borderTopRightRadius: 8,
                    minHeight: 30,
                    '& .MuiAccordionSummary-content': {
                      alignItems: 'center',
                    },
                  }}
                >
                  <Box
                    sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}
                  >
                    <Typography fontWeight={500}>
                      {loc.locationName || `Location ${idx + 1}`}
                    </Typography>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={(e) => {
                        e.stopPropagation();
                        setLocations((prev) => prev.filter((_, i) => i !== idx));
                      }}
                    >
                      <DeleteOutline />
                    </IconButton>
                  </Box>
                </AccordionSummary>

                <AccordionDetails
                  sx={{
                    // match the bottom corners
                    borderBottomLeftRadius: 8,
                    borderBottomRightRadius: 8,
                    backgroundColor: '#fff',
                  }}
                >
                  <Stack spacing={2}>
                    <TextField
                      size="small"
                      fullWidth
                      label="Location Name"
                      required
                      error={!!(errors.locations[idx]?.locationName)}
                      helperText={errors.locations[idx]?.locationName}
                      value={loc.locationName}
                      onChange={(e) =>
                        handleLocationChange(idx, 'locationName', e.target.value)
                      }
                    />
                    <TextField
                      size="small"
                      fullWidth
                      label="Latitude"
                      required
                      error={!!(errors.locations[idx]?.latitude)}
                      helperText={errors.locations[idx]?.latitude}
                      value={loc.latitude}
                      onChange={(e) =>
                        handleLocationChange(idx, 'latitude', e.target.value)
                      }
                    />
                    <TextField
                      size="small"
                      fullWidth
                      label="Longitude"
                      value={loc.longitude}
                      required
                      error={!!(errors.locations[idx]?.longitude)}
                      helperText={errors.locations[idx]?.longitude}
                      onChange={(e) =>
                        handleLocationChange(idx, 'longitude', e.target.value)
                      }
                    />
                    <FormControl required error={!!(errors.locations[idx]?.devices)}
                      fullWidth size="small">
                      <InputLabel>Devices</InputLabel>
                      <Select
                        multiple
                        value={loc.devices} // an array of _id strings
                        label="Devices"
                        onChange={(e) =>
                          handleLocationChange(idx, 'devices', e.target.value)
                        }
                        renderValue={(selected) =>
                          selected
                            .map((id) => {
                              const dev = devicesList.find((d) => d._id === id);
                              return dev?.device_id || id;
                            })
                            .join(', ')
                        }
                        MenuProps={{ PaperProps: { style: { maxHeight: 250 } } }}
                      >
                        {getAvailableDevices(idx).map((dev) => (
                          <MenuItem key={dev._id} value={dev._id}>
                            {dev.device_id} ({dev.device_category})
                          </MenuItem>
                        ))}
                      </Select>
                      {errors.locations[idx]?.devices && (
                        <FormHelperText>{errors.locations[idx].devices}</FormHelperText>
                      )}
                    </FormControl>

                    {!loc.saved && (
                      <Button
                        variant="outlined"
                        onClick={() => handleSaveLocation(idx)}
                      >
                        Save Location
                      </Button>
                    )}
                  </Stack>

                </AccordionDetails>
              </Accordion>
            ))}
          </Box>

          <Box
            sx={{
              pt: 2,
              maxHeight: 350,
              overflow: 'auto',
              '&::-webkit-scrollbar': { display: 'none' },
            }}
          >
            <FormControl sx={{
              mb: 1.5
            }} fullWidth size="small" error={!!errors.currency} required>
              <InputLabel>Currency</InputLabel>
              <Select
                value={formState.currency}
                label="Currency"
                onChange={(e) => updateField('currency', e.target.value)}
              >
                {Object.entries(currencySymbols).map(([code, symbol]) => (
                  <MenuItem key={code} value={code}>
                    {code}
                  </MenuItem>
                ))}
              </Select>
              {errors.currency && <FormHelperText>{errors.currency}</FormHelperText>}
            </FormControl>

            {formState.timePricePairs.map((pair, index) => (
              <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <TextField
                  label="Time"
                  size="small"
                  required
                  error={!!errors.timePricePairs[index]?.time}
                  helperText={errors.timePricePairs[index]?.time}
                  sx={{ flex: 1 }}
                  value={pair.time}
                  InputProps={{
                    endAdornment : (
                      <InputAdornment position='start'>
                        <span style={{ fontSize : "0.8rem" , color : '#999'}}>
                          min
                        </span>
                      </InputAdornment>
                    ),
                  }}
                  onChange={(e) => handleTimePrice(index, 'time', e.target.value)}
                />
                <TextField
                  label={`Price (${currencySymbols[formState.currency] || ''})`}
                  size="small"
                  required
                  error={!!errors.timePricePairs[index]?.price}
                  helperText={errors.timePricePairs[index]?.price}
                  sx={{ flex: 1 }}
                  value={pair.price}
                  onChange={(e) => handleTimePrice(index, 'price', e.target.value)}
                />
                {index === formState.timePricePairs.length - 1 && (
                  <IconButton size="small" onClick={handleAddTimePrice}>
                    <AddCircleOutline />
                  </IconButton>
                )}
                {formState.timePricePairs.length > 1 && (
                  <IconButton size="small" onClick={() => handleRemoveTimePrice(index)}>
                    <RemoveCircleOutline />
                  </IconButton>
                )}
              </Box>
            ))}

          </Box>


        </Stack>
      </RightDrawer>

      <Snackbar
        open={alert.open}
        autoHideDuration={4000}
        onClose={() => setAlert({ ...alert, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          severity={alert.type}
          variant="filled"
          onClose={() => setAlert({ ...alert, open: false })}
          sx={{ width: '100%' }}
        >
          {alert.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DeviceVendorMapping;
